import { UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, scan, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslationKey, TranslationModule, TranslationService } from '@lib/translation';
import { SnIconModule } from '@lib/ui/icon';

import { IFsLprProviderInterface } from '../../../../../api';
import { ViLPRProvider } from '../../../../../shared/models';
import { LPRProviderNameMapperPipe } from '../../../../../shared/pipes';
import { LPRProviderSelection, LPRSourceProviderConflicts } from '../../models';
import { LPRSourceSelectService } from '../../services';

/**
 * Displays a warning icon with tooltip when selected sources have issues
 * Shows connection errors, filter incompatibilities, or partial match warnings
 * Only appears when at least one selected source has problems
 */
@UntilDestroy()
@Component({
  selector: 'fs-lpr-source-conflicts',
  standalone: true,
  templateUrl: './lpr-source-conflicts.component.html',
  styleUrls: ['./lpr-source-conflicts.component.scss'],
  imports: [CommonModule, MatTooltipModule, SnIconModule, TranslationModule],
  providers: [LPRProviderNameMapperPipe],
})
export class LPRSourceConflictsComponent implements OnInit {
  private static readonly NEWLINE = '\n';
  private static readonly DOUBLE_NEWLINE = '\n\n';
  private static readonly BULLET_POINT = '•';

  @Input() lprSourceSelectService: LPRSourceSelectService;
  @Input() providers$: Observable<IFsLprProviderInterface[]>;
  @Input() providerSelectionChange$: Observable<LPRProviderSelection>;
  @Input() shouldShowPartialMatches$: Observable<boolean>;

  translationService = inject(TranslationService);
  lprProviderNameMapperPipe = inject(LPRProviderNameMapperPipe);

  conflicts$: Observable<LPRSourceProviderConflicts[]>;
  conflictsTooltipText$: Observable<string>;

  ngOnInit(): void {
    this.setConflictsSubscription();
    this.setConflictsTooltipTextSubscription();
  }

  private setConflictsSubscription(): void {
    const providerSelections$ = this.providerSelectionChange$.pipe(
      scan((selections, change) => {
        const newSelections = new Map(selections);
        newSelections.set(change.name, change.isSelected);
        return newSelections;
      }, new Map<ViLPRProvider, boolean>()),
      startWith(new Map<ViLPRProvider, boolean>()),
      shareReplay(1),
    );

    this.conflicts$ = combineLatest([
      this.lprSourceSelectService.areAllProvidersLoaded$,
      this.shouldShowPartialMatches$,
      providerSelections$,
    ]).pipe(
      filter(([areAllProvidersLoaded]) => areAllProvidersLoaded),
      withLatestFrom(this.providers$),
      map(([[_, shouldShowPartialMatches, providerSelections], providers]) => {
        return providers
          .filter((provider) => provider.nameEnum !== undefined)
          .map((provider) => {
            const errors = [...(provider.errors ?? [])];

            const warnings = shouldShowPartialMatches
              ? []
              : (provider.warnings ?? []).map((w) => w.message);

            return {
              provider: provider.nameEnum,
              errors,
              warnings,
            } as LPRSourceProviderConflicts;
          })
          .filter((entry) => {
            const isSelected = providerSelections.get(entry.provider);

            if (isSelected === false) {
              return false;
            }

            return entry.errors.length > 0 || entry.warnings.length > 0;
          });
      }),
      shareReplay(1),
    );
  }

  private setConflictsTooltipTextSubscription(): void {
    this.conflictsTooltipText$ = this.conflicts$.pipe(
      map((conflicts) => this.formatConflictsTooltip(conflicts)),
    );
  }

  private formatConflictsTooltip(conflicts: LPRSourceProviderConflicts[]): string {
    if (!conflicts || conflicts.length === 0) {
      return '';
    }

    return (
      conflicts
        .map((conflict) => {
          const providerName = this.translationService.translate(
            this.lprProviderNameMapperPipe.transform(
              conflict.provider,
            ) as unknown as TranslationKey,
          );

          const lines: string[] = [`${providerName}:`];

          lines.push(
            ...conflict.errors.map(
              (error) =>
                `${LPRSourceConflictsComponent.BULLET_POINT} ${this.translationService.translate(error as unknown as TranslationKey)}`,
            ),
          );

          lines.push(
            ...conflict.warnings.map(
              (warning) =>
                `${LPRSourceConflictsComponent.BULLET_POINT} ${this.translationService.translate(warning as unknown as TranslationKey)}`,
            ),
          );

          return lines.join(LPRSourceConflictsComponent.NEWLINE);
        })
        .join(LPRSourceConflictsComponent.DOUBLE_NEWLINE) + LPRSourceConflictsComponent.NEWLINE
    );
  }
}
